// `{name}` placeholders + ICU-lite plurals for dictionary strings (round2-i18n.md
// §6 "Interpolation" and "Plurals / counted nouns"). Replaces the designs'
// `.replace('{n}', …)` chains and the admin concatenation+regex approach.
//
//   interpolate('en', '{n} of {total}', { n: 3, total: 12 })          → "3 of 12"
//   interpolate('en', '{n, plural, one{# booking} other{# bookings}}', { n: 1 })
//                                                                      → "1 booking"
//   formatMessage('bn', 'format.count.of', { n: 3, total: 12 })        → "১২-এর মধ্যে ৩"
//
// Parameter values:
//   number    → formatNumber(lang, n): grouped, Bangla digits under bn.
//   string    → inserted verbatim (names, already-formatted parts).
//   AsciiCode → inserted verbatim, never localised (phones, TRX, codes, OTP).
//
// Plural rule: en selects `one` for exactly 1, else `other`. bn nouns do not
// inflect for number — the designs use the "টি"/"জন" classifier in every case
// ("১টি বুকিং", "৫টি বুকিং") — so bn always selects `other`; bn dictionary values
// normally skip plural syntax entirely ('{n}টি বুকিং'). `=N{…}` exact matches are
// honoured in both languages (e.g. `=0{No bookings}`). Inside a branch, `#` is the
// formatted count.
//
// An unknown placeholder is left as-is (`{name}`) so a missing param is visible.
import type { Lang } from './translate';
import { t } from './translate';
import type { MessageKey } from './message-key';
import { AsciiCode } from './ascii-codes';
import { formatNumber } from './format-number';

export type MessageParam = string | number | AsciiCode;
export type MessageParams = Readonly<Record<string, MessageParam>>;

export type PluralCategory = 'one' | 'other';

export function pluralCategory(lang: Lang, n: number): PluralCategory {
  if (lang === 'bn') return 'other';
  return n === 1 ? 'one' : 'other';
}

function renderParam(lang: Lang, value: MessageParam): string {
  if (value instanceof AsciiCode) return value.value;
  if (typeof value === 'number') return formatNumber(lang, value);
  return value;
}

// Index of the `}` matching the `{` at `open`, or -1.
function matchBrace(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    if (s[i] === '{') depth++;
    else if (s[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

// Parses "one{…} other{…} =0{…}" into selector → body.
function parseBranches(src: string): Map<string, string> {
  const out = new Map<string, string>();
  let i = 0;
  while (i < src.length) {
    const rest = src.slice(i);
    const m = /^\s*(=\d+|zero|one|two|few|many|other)\s*\{/.exec(rest);
    if (!m) break;
    const open = i + m[0].length - 1;
    const close = matchBrace(src, open);
    if (close < 0) break;
    out.set(m[1] ?? '', src.slice(open + 1, close));
    i = close + 1;
  }
  return out;
}

function renderPlural(
  lang: Lang,
  name: string,
  body: string,
  params: MessageParams,
): string | null {
  const raw = params[name];
  if (raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : Number(raw instanceof AsciiCode ? raw.value : raw);
  const branches = parseBranches(body);
  const chosen =
    (Number.isFinite(n) ? branches.get(`=${n}`) : undefined) ??
    (Number.isFinite(n) ? branches.get(pluralCategory(lang, n)) : undefined) ??
    branches.get('other');
  if (chosen === undefined) return null;
  const count = Number.isFinite(n) ? formatNumber(lang, n) : String(raw);
  return interpolate(lang, chosen.replace(/#/g, count), params);
}

export function interpolate(lang: Lang, template: string, params: MessageParams = {}): string {
  let out = '';
  let i = 0;
  while (i < template.length) {
    const open = template.indexOf('{', i);
    if (open < 0) {
      out += template.slice(i);
      break;
    }
    out += template.slice(i, open);
    const close = matchBrace(template, open);
    if (close < 0) {
      out += template.slice(open);
      break;
    }
    const inner = template.slice(open + 1, close);
    const whole = template.slice(open, close + 1);
    const plural = /^\s*(\w+)\s*,\s*plural\s*,([\s\S]*)$/.exec(inner);
    const simple = /^\s*(\w+)\s*$/.exec(inner);
    if (plural) {
      out += renderPlural(lang, plural[1] ?? '', plural[2] ?? '', params) ?? whole;
    } else if (simple) {
      const v = params[simple[1] ?? ''];
      out += v === undefined ? whole : renderParam(lang, v);
    } else {
      out += whole;
    }
    i = close + 1;
  }
  return out;
}

// Dictionary lookup + interpolation in one call.
export function formatMessage(lang: Lang, key: MessageKey, params: MessageParams = {}): string {
  return interpolate(lang, t(lang, key), params);
}
